import { createReducer } from '@reduxjs/toolkit';
import InitialState from '../reducers/initial-state';
import { mergeCasesActions, AmplifyCore } from '@codametrix/ui-common';
import { logout, onLocationChange } from '../actions/ui';
import { jumpContext } from '../actions/contexts.action';
import { logoutApp, login } from '../actions/login.action';

const { benchEditable: benchEditableCore } = AmplifyCore;

const reducer = createReducer(InitialState.mergeCases, builder => {
  builder.addCase(logout.type, () => ({ ...InitialState.mergeCases }));
  builder.addCase(jumpContext.pending.type, () => ({ ...InitialState.mergeCases }));

  builder.addCase(mergeCasesActions.toggleMergeableCaseSelection, (state, action) => ({
    ...state,
    mergeableCaseRuns: state.mergeableCaseRuns.map(mergeableCaseRun => {
      if (mergeableCaseRun?.process_instance_guid === action.payload) {
        return { ...mergeableCaseRun, selected: !mergeableCaseRun.selected };
      }
      return mergeableCaseRun;
    })
  }));

  builder.addCase(mergeCasesActions.setActiveCaseRun, (state, action) => ({
    ...state,
    activeProcessInstanceGuid: action.payload
  }));

  builder.addCase(mergeCasesActions.showMergeCasesModal, (state, action) => {
    const { caseRun: activeItem, cursorAPIContext } = action.payload;
    const primaryActiveItem = { ...activeItem, primary: true };
    return {
      ...state,
      isOpen: true,
      loadingNotes: false,
      loadingMerging: false,
      loadingMergeableCases: false,
      mergingResult: false,
      activeHTMLNotes: [],
      activeNotes: [],
      mergeableCaseRuns: [],
      activeProcessInstanceGuid: primaryActiveItem.process_instance_guid,
      currentCaseRun: addNoteType(primaryActiveItem),
      cursorAPIContext
    };
  });

  const resetModal = (state: typeof InitialState.mergeCases) => ({
    ...state,
    isOpen: false,
    loadingNotes: false,
    loadingMerging: false,
    loadingMergeableCases: false,
    mergingResult: false,
    activeProcessInstanceGuid: null,
    currentCaseRun: {} as CMxAPI.CaseRunV2,
    activeHTMLNotes: [],
    activeNotes: []
  });

  builder.addCase(mergeCasesActions.hideMergeCasesModal.type, resetModal);
  builder.addCase('AUTH_LOGOUT/pending', resetModal);
  builder.addCase('LOGIN/pending', resetModal);
  builder.addCase(onLocationChange.type, resetModal);

  builder.addCase(mergeCasesActions.requestMergeCases.pending, state => ({
    ...state,
    loadingMerging: true
  }));

  builder.addCase(mergeCasesActions.requestMergeCases.rejected, state => ({
    ...state,
    loadingMerging: false
  }));

  builder.addCase(mergeCasesActions.requestMergeCases.fulfilled, state => ({
    ...state,
    loadingMerging: false,
    mergingResult: true
  }));

  builder.addCase(mergeCasesActions.fetchMergeableCases.pending, state => ({
    ...state,
    loadingMergeableCases: true
  }));

  builder.addCase(mergeCasesActions.fetchMergeableCases.rejected, state => ({
    ...state,
    loadingMergeableCases: false
  }));

  builder.addCase(mergeCasesActions.fetchMergeableCases.fulfilled, (state, action) => {
    const mergeableCaseRuns = action.payload;
    const { roles, userContext } = AmplifyCore.state.codeBenchState;

    return {
      ...state,
      loadingMergeableCases: false,
      mergeableCaseRuns: mergeableCaseRuns
        ? mergeableCaseRuns
            .map(mergeableCaseRun =>
              addNoteType({
                ...mergeableCaseRun,
                selected: false,
                primary: false,
                tenant_name: state.currentCaseRun.tenant_name,
                processStatus: benchEditableCore.determineProcessStatus(
                  {
                    isChargeable: mergeableCaseRun?.coding_status?.is_chargeable,
                    processInstanceStatus:
                      mergeableCaseRun?.coding_status?.process_instance_status,
                    holdReason: mergeableCaseRun?.coding_status?.hold_status,
                    holdComment: mergeableCaseRun?.coding_status?.hold_comment,
                    qaStatus: mergeableCaseRun?.coding_status?.qa_status,
                    cancelled: mergeableCaseRun?.coding_status?.is_cancelled,
                    roles,
                    userContext
                  },
                  mergeableCaseRun?.coding_status !== null,
                  mergeableCaseRun?.parent_caserun_uid ?? null
                )
              })
            )
            .filter(
              mergeableCaseRun =>
                mergeableCaseRun.process_instance_guid !==
                state.currentCaseRun.process_instance_guid
            )
        : []
    };
  });

  builder.addCase(mergeCasesActions.getDocumentsByCaseRun.pending, state => ({
    ...state,
    loadingNotes: true,
    activeHTMLNotes: [],
    activeNotes: []
  }));

  builder.addCase(mergeCasesActions.getDocumentsByCaseRun.fulfilled, (state, action) => ({
    ...state,
    loadingNotes: false,
    activeHTMLNotes: action.payload.htmlNotes,
    activeNotes: action.payload.htmlNotes
  }));

  builder.addCase(mergeCasesActions.getDocumentsByCaseRun.rejected, state => ({
    ...state,
    loadingNotes: false,
    activeHTMLNotes: [],
    activeNotes: []
  }));
});

const addNoteType = (activeItem: CMxAPI.CaseRunV2) => {
  const noteType = activeItem?.notes[0] ?? {};
  return {
    ...activeItem,
    note_type: (noteType as CMxAPI.Note)?.description ?? ''
  };
};

export default reducer;
